import './util/handleError';
import './util/setupServiceWorker';
import './global/init';
import './util/dev/renderStormHarness';

import { maybeRetirePublicRootServiceWorkerOnBoot } from './util/publicRootServiceWorkerRetirement';
import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal, setGlobal,
} from './global';
import { callApi } from './api/gramjs/worker/connector';
import { getStreams, hasPhoneCallConnection } from './lib/secret-sauce/index';

// Expose for Playwright testing
(window as any).__callApi = callApi;
(window as any).__getActions = getActions;
(window as any).__getGlobal = getGlobal;
(window as any).__setGlobal = setGlobal;

import {
  DEBUG, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { selectTabState } from './global/selectors';
import { selectSharedSettings } from './global/selectors/sharedState';
import { betterView } from './util/betterView';
import { IS_TAURI } from './util/browser/globalEnvironment';
import listenOtherClients from './util/browser/listenOtherClients';
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/browser/multitab';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { MULTITAB_STORAGE_KEY } from './util/multiaccount';
import { getWindowLocalStorage } from './util/browserStorage';
import { checkAndAssignPermanentWebVersion } from './util/permanentWebVersion';
import { getBuildGenerationGateValue } from './util/buildGeneration';
import { onBeforeUnload } from './util/schedulers';
import updateWebmanifest from './util/updateWebmanifest';
import { storePendingPhoneCallSnapshot } from './global/phoneCallReloadRecovery';
import { shouldDiscardPhoneCallOnPageUnload } from './global/actions/api/phoneCallUnloadPolicy';
import {
  shouldSelfHealStaleActivePhoneCall,
} from './global/phoneCallSelfHeal';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

const isRetiringPublicRootServiceWorker = maybeRetirePublicRootServiceWorkerOnBoot();

if (IS_TAURI) {
  Promise.all([
    import('./util/tauri/initTauriApi'),
    import('./util/tauri/setupTauriListeners'),
  ]).then(([{ default: initTauriApi }, { default: setupTauriListeners }]) => {
    initTauriApi();
    setupTauriListeners();
  });
}

if (!isRetiringPublicRootServiceWorker) {
  init();
}

let staleActivePhoneCallStartedAt: number | undefined;

function startPhoneCallSelfHealMonitor() {
  (window as any).__runaiPhoneCallSelfHealDebug = {
    started: true,
    phoneCallState: undefined,
    isConnected: undefined,
    hasConnection: false,
    hasLocalStreams: false,
    staleForMs: 0,
  };
  window.setInterval(() => {
    try {
      const global = getGlobal();
      const phoneCall = global.phoneCall;
      const hasLocalStreams = Boolean(getStreams()?.audio || getStreams()?.video || getStreams()?.presentation);
      const hasConnection = hasPhoneCallConnection();

      if (phoneCall?.state === 'active' && !phoneCall.isConnected && (!hasConnection || !hasLocalStreams)) {
        staleActivePhoneCallStartedAt = staleActivePhoneCallStartedAt ?? Date.now();
        const staleForMs = Date.now() - staleActivePhoneCallStartedAt;
        (window as any).__runaiPhoneCallSelfHealDebug = {
          started: true,
          phoneCallState: phoneCall.state,
          isConnected: phoneCall.isConnected,
          hasConnection,
          hasLocalStreams,
          staleForMs,
        };
        if (shouldSelfHealStaleActivePhoneCall({
          phoneCallState: phoneCall.state,
          isConnected: phoneCall.isConnected,
          hasConnection,
          hasLocalStreams,
          staleForMs,
        })) {
          // eslint-disable-next-line no-console
          console.warn('[CALL_SELF_HEAL] hanging up stale active call', (window as any).__runaiPhoneCallSelfHealDebug);
          getActions().hangUp?.();
        }
        return;
      }

      (window as any).__runaiPhoneCallSelfHealDebug = {
        started: true,
        phoneCallState: phoneCall?.state,
        isConnected: phoneCall?.isConnected,
        hasConnection,
        hasLocalStreams,
        staleForMs: 0,
      };
      staleActivePhoneCallStartedAt = undefined;
    } catch (error) {
      (window as any).__runaiPhoneCallSelfHealDebug = {
        started: true,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }, 1000);
}

async function init() {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  checkAndAssignPermanentWebVersion();
  listenOtherClients();

  subscribeToMultitabBroadcastChannel();
  await requestGlobal(getBuildGenerationGateValue(APP_VERSION, APP_REVISION));
  const storage = getWindowLocalStorage();
  storage?.setItem(MULTITAB_STORAGE_KEY, '1');
  onBeforeUnload(() => {
    const global = getGlobal();
    const tabStateById = global?.byTabId;
    if (tabStateById && Object.keys(tabStateById).length === 1) {
      storage?.removeItem(MULTITAB_STORAGE_KEY);
    }
  });

  await initGlobal();
  getActions().init();

  getActions().updateShouldEnableDebugLog();
  getActions().updateShouldDebugExportedSenders();

  const global = getGlobal();

  initLocalization(selectSharedSettings(global).language, true);

  subscribeToMasterChange((isMasterTab) => {
    getActions()
      .switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true });
  });
  const shouldReestablishMasterToSelf = getGlobal().auth.state !== 'authorizationStateReady';
  establishMultitabRole(shouldReestablishMasterToSelf);
  startPhoneCallSelfHealMonitor();

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    updateWebmanifest();

    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );

    betterView();
  });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(getGlobal()));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', getGlobal());
    });
  }
}

onBeforeUnload(() => {
  if (isRetiringPublicRootServiceWorker) return;
  const global = getGlobal();
  if (global.phoneCall && !shouldDiscardPhoneCallOnPageUnload(global.phoneCall.state)) {
    storePendingPhoneCallSnapshot(global.phoneCall);
  }
  const actions = getActions();
  actions.leaveGroupCall?.({ isPageUnload: true });
  actions.hangUp?.({ isPageUnload: true });
});

window.addEventListener('pagehide', () => {
  if (isRetiringPublicRootServiceWorker) return;
  const global = getGlobal();
  if (global.phoneCall && !shouldDiscardPhoneCallOnPageUnload(global.phoneCall.state)) {
    storePendingPhoneCallSnapshot(global.phoneCall);
  }
});
